/**
 * Schule (Roadmap F-22): Faecher und Einstellungen der Schulseite.
 * Hausaufgaben sind normale Aufgaben mit `subjectId` - siehe tasks.ts.
 */
import { db } from '@/db/schema';
import { AREA_COLORS } from '@/domain/areaColors';
import { setzeWoche } from '@/domain/school/weeks';
import { ersterTag, stundenRegel, type StundenWoche } from '@/domain/school/timetable';
import { ferienGueltig } from '@/domain/school/ferien';
import { createEvent, deleteEvent } from '@/db/repositories/events';
import { inDenPapierkorb, papierkorbTitel } from '@/db/repositories/papierkorb';
import { nowInstant, today } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import type { CalendarEvent, ID, LehrplanStatus, LehrplanThema, SchoolSettings, Subject } from '@/domain/types';

const SCHULE = 'schule' as const;

/**
 * Legt beim ersten Oeffnen der Schulseite die Einstellungen an - mit den
 * Werten aus dem bisherigen Schulcockpit: Punkte 0-15, A/B-Wochen an, diese
 * Woche ist A, Fach Chemie. Ein vorhandener Bereich "Schule..." wird
 * genommen. Laeuft es ein zweites Mal, passiert nichts.
 */
export async function ensureSchoolSetup(): Promise<void> {
  await db.transaction('rw', db.schoolSettings, db.subjects, db.areas, async () => {
    if (await db.schoolSettings.get(SCHULE)) return;
    const bereich = (await db.areas.toArray()).find((a) => !a.archived && /^schule/i.test(a.name.trim()));
    await db.schoolSettings.add({
      id: SCHULE,
      notensystem: 'punkte',
      abWochen: true,
      aWocheAb: setzeWoche(today(), 'A'),
      areaId: bereich?.id ?? null,
      updatedAt: nowInstant(),
    });
    if ((await db.subjects.count()) === 0) await createSubject('Chemie');
  });
}

export async function updateSchoolSettings(patch: Partial<Omit<SchoolSettings, 'id' | 'updatedAt'>>): Promise<void> {
  await db.schoolSettings.update(SCHULE, { ...patch, updatedAt: nowInstant() });
}

/** Ferien eintragen (F-22) - nur mit gueltigem Zeitraum. */
export async function addFerien(name: string, von: string, bis: string): Promise<boolean> {
  if (!ferienGueltig(von, bis)) return false;
  return db.transaction('rw', db.schoolSettings, async () => {
    const s = await db.schoolSettings.get(SCHULE);
    if (!s) return false;
    const neu = { id: newId(), name: name.trim().slice(0, 80) || 'Ferien', von, bis };
    const ferien = [...(s.ferien ?? []), neu].sort((a, b) => a.von.localeCompare(b.von));
    await db.schoolSettings.update(SCHULE, { ferien, updatedAt: nowInstant() });
    return true;
  });
}

export async function removeFerien(id: ID): Promise<void> {
  const name = (await db.schoolSettings.get(SCHULE))?.ferien?.find((f) => f.id === id)?.name;
  await inDenPapierkorb({ art: 'entfernt', tabelle: 'schoolSettings', zeileIds: [SCHULE], titel: papierkorbTitel(name) ?? 'Ferien' }, () => db.transaction('rw', db.schoolSettings, async () => {
    const s = await db.schoolSettings.get(SCHULE);
    if (!s) return;
    await db.schoolSettings.update(SCHULE, { ferien: (s.ferien ?? []).filter((f) => f.id !== id), updatedAt: nowInstant() });
  }));
}

export async function createSubject(name: string, color?: string): Promise<Subject | null> {
  const titel = name.trim();
  if (!titel) return null;
  const alle = await db.subjects.toArray();
  const ts = nowInstant();
  const fach: Subject = {
    id: newId(),
    name: titel,
    color: color ?? AREA_COLORS[alle.length % AREA_COLORS.length]!.value,
    order: alle.reduce((m, s) => Math.max(m, s.order), -1) + 1,
    archived: false,
    createdAt: ts,
    updatedAt: ts,
  };
  await db.subjects.add(fach);
  return fach;
}

export async function updateSubject(id: ID, patch: Partial<Pick<Subject, 'name' | 'color' | 'archived'>>): Promise<void> {
  if (patch.name !== undefined && !patch.name.trim()) return;
  const name = patch.name?.trim();
  await db.transaction('rw', db.subjects, db.events, async () => {
    const ts = nowInstant();
    await db.subjects.update(id, { ...patch, ...(name ? { name } : {}), updatedAt: ts });
    // Schulstunden heissen wie ihr Fach - beim Umbenennen ziehen sie mit.
    if (name) {
      // Klausuren behalten ihren eigenen Titel.
      await db.events.filter((e) => e.subjectId === id && !e.klausur).modify((e) => { e.title = name; e.updatedAt = ts; });
    }
  });
}

export interface NeueStunde {
  subjectId: ID;
  /** 1 = Montag ... 5 = Freitag */
  tag: number;
  /** 'HH:mm' */
  von: string;
  bis: string;
  raum?: string;
  woche: StundenWoche;
}

/**
 * Legt eine Schulstunde als woechentlichen Serientermin an (Stufe 2).
 * Sie steht damit ganz normal im Kalender und laesst sich dort aendern.
 */
export async function createLesson(stunde: NeueStunde): Promise<CalendarEvent | null> {
  const [fach, settings] = await Promise.all([db.subjects.get(stunde.subjectId), db.schoolSettings.get(SCHULE)]);
  if (!fach || !settings || !(stunde.von < stunde.bis)) return null;
  // Ohne A/B-Wochen gibt es nur "jede Woche".
  const woche = settings.abWochen ? stunde.woche : 'jede';
  const tag = ersterTag(stunde.tag, woche, today(), settings.aWocheAb);
  return createEvent({
    title: fach.name,
    allDay: false,
    start: `${tag}T${stunde.von}`,
    end: `${tag}T${stunde.bis}`,
    tz: 'Europe/Berlin',
    rrule: stundenRegel(woche),
    areaId: settings.areaId,
    subjectId: fach.id,
    ...(stunde.raum?.trim() ? { location: stunde.raum.trim() } : {}),
  });
}

/** Entfernt die Stunde samt allen Ausnahmen der Serie - als EIN Papierkorb-Eintrag (F-45). */
export async function deleteLesson(eventId: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'events', zeileIds: [eventId] }, () => db.transaction('rw', db.events, db.tasks, db.terminBestaetigungen, async () => {
    await db.events.where('recurringEventId').equals(eventId).delete();
    await deleteEvent(eventId);
  }));
}

// ---------------------------------------------------------------------------
// Lehrplan (Stufe 5) - Themen leben am Fach.
// ---------------------------------------------------------------------------

async function lehrplanAendern(subjectId: ID, fn: (l: LehrplanThema[]) => LehrplanThema[]): Promise<void> {
  await db.subjects.where('id').equals(subjectId).modify((f) => {
    f.lehrplan = fn(f.lehrplan ?? []);
    f.updatedAt = nowInstant();
  });
}

export async function addLehrplanThema(subjectId: ID, text: string): Promise<void> {
  const t = text.trim();
  if (!t) return;
  await lehrplanAendern(subjectId, (l) => [...l, { id: newId(), text: t, status: 'offen' }]);
}

export async function setLehrplanStatus(subjectId: ID, themaId: ID, status: LehrplanStatus): Promise<void> {
  await lehrplanAendern(subjectId, (l) => l.map((t) => (t.id === themaId ? { ...t, status } : t)));
}

export async function removeLehrplanThema(subjectId: ID, themaId: ID): Promise<void> {
  const thema = (await db.subjects.get(subjectId))?.lehrplan?.find((t) => t.id === themaId);
  await inDenPapierkorb({ art: 'entfernt', tabelle: 'subjects', zeileIds: [subjectId], titel: papierkorbTitel(thema?.text) },
    () => lehrplanAendern(subjectId, (l) => l.filter((t) => t.id !== themaId)));
}
